import PropTypes from 'prop-types'
import {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { getFieldRemoved, isInBound, isPresent, noop } from 'utils'
import Draggable from 'components/Draggable'
import classNames from 'classnames'
import { useUpdatingRef } from 'utils/hooks/useUpdatingRef'

const droppableContext = createContext({
  isProviderSet: false,
  startDragging: noop,
  finishDragging: noop,
  getActiveValues: noop,
})

const Provider = ({ children }) => {
  const [activeItems, setActiveItems] = useState({})

  const startDragging = useCallback(
    ({ id, type, value }) =>
      setActiveItems(_activeItems => ({
        ..._activeItems,
        [id]: {
          type,
          value,
        },
      })),
    [setActiveItems]
  )

  const finishDragging = useCallback(
    id => setActiveItems(_activeItems => getFieldRemoved(id)(_activeItems)),
    []
  )

  const getActiveValues = useCallback(
    acceptableTypes => {
      const draggingItems = Object.values(activeItems || {})
      const acceptableItems = isPresent(acceptableTypes)
        ? draggingItems.filter(item =>
            acceptableTypes.find(type => item.type === type)
          )
        : draggingItems
      return acceptableItems.map(item => item.value)
    },
    [activeItems]
  )

  return (
    <droppableContext.Provider
      value={{
        isProviderSet: true,
        startDragging,
        finishDragging,
        getActiveValues,
      }}>
      {children}
    </droppableContext.Provider>
  )
}

const Area = forwardRef(
  (
    {
      accepts,
      onDragOver = noop,
      onDragOut = noop,
      onDrop = noop,
      className = '',
      classNameOnDragOver = '',
      children,
      ...attrs
    },
    outerRef
  ) => {
    const innerRef = useRef()
    const onDropRef = useUpdatingRef(onDrop)
    const onDragOutRef = useUpdatingRef(onDragOut)
    const onDragOverRef = useUpdatingRef(onDragOver)
    const ref = outerRef || innerRef
    const { isProviderSet, getActiveValues } = useContext(droppableContext)
    const [isDragOver, setDragOver] = useState(false)
    const getActiveValuesRef = useUpdatingRef(getActiveValues)

    if (!isProviderSet && process.env.NODE_ENV === 'development') {
      console.warn(
        'Droppable.Area should be under the Droppable.Provider to work properly'
      )
    }

    const handleDragIn = useCallback(
      e => {
        setDragOver(isInBound(ref.current, e.clientX, e.clientY))
      },
      [ref]
    )

    const handleDrop = useCallback(() => {
      if (isDragOver) {
        onDropRef.current(getActiveValues(accepts))
      }
      setDragOver(false)
      window.document.removeEventListener('mouseup', handleDrop)
      window.document.removeEventListener('mousemove', handleDragIn)
    }, [isDragOver, handleDragIn, onDropRef, getActiveValues, accepts])

    useEffect(() => {
      if (isPresent(getActiveValues(accepts))) {
        window.document.addEventListener('mousemove', handleDragIn)
        window.document.addEventListener('mouseup', handleDrop)
      }
      return () => {
        window.document.removeEventListener('mousemove', handleDragIn)
        window.document.removeEventListener('mouseup', handleDrop)
      }
    }, [getActiveValues, handleDrop, handleDragIn, accepts])

    useEffect(() => {
      ;(isDragOver ? onDragOverRef.current : onDragOutRef.current)(
        getActiveValuesRef.current()
      )
    }, [getActiveValuesRef, isDragOver, onDragOutRef, onDragOverRef])

    return cloneElement(Children.only(children), {
      ref,
      className: classNames(Children.only(children).props.className, {
        [classNameOnDragOver]: isDragOver,
      }),
      ...attrs,
    })
  }
)

Area.displayName = 'Droppable.Area'

const Item = forwardRef(
  (
    {
      id,
      type,
      value,
      tag = 'div',
      onDragStart = noop,
      onDragEnd = noop,
      zIndexOnDrag,
      resetOnDragEnd,
      style,
      className,
      classNameOnDrag,
      children,
      ...extraProps
    },
    ref
  ) => {
    const { startDragging, finishDragging } = useContext(droppableContext)
    return (
      <Draggable
        ref={ref}
        onDragStart={() => {
          startDragging({
            id,
            type,
            value,
          })
          onDragStart()
        }}
        onDragEnd={() => {
          finishDragging(id)
          onDragEnd()
        }}
        zIndexOnDrag={zIndexOnDrag}
        resetOnDragEnd={resetOnDragEnd}
        className={className}
        classNameOnDrag={classNameOnDrag}
        style={style}
        {...extraProps}>
        {Children.only(children)}
      </Draggable>
    )
  }
)

Item.displayName = 'Droppable.Item'

const Droppable = {
  Provider,
  Item,
  Area,
}

Droppable.Provider.displayName = 'Droppable.Provider'
Droppable.Item.displayName = 'Droppable.Item'
Droppable.Area.displayName = 'Droppable.Area'

export default Droppable

Area.propTypes = {
  accepts: PropTypes.array,
  children: PropTypes.node,
  className: PropTypes.string,
  classNameOnDragOver: PropTypes.string,
  onDragOut: PropTypes.func,
  onDragOver: PropTypes.func,
  onDrop: PropTypes.func,
}

Item.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  classNameOnDrag: PropTypes.string,
  id: PropTypes.string.isRequired,
  onDragEnd: PropTypes.func,
  onDragStart: PropTypes.func,
  resetOnDragEnd: PropTypes.bool,
  style: PropTypes.object,
  tag: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.any,
  zIndexOnDrag: PropTypes.number,
}

Provider.propTypes = {
  children: PropTypes.node,
}
